import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://filmcable.net';
const SITE_NAME = 'Filmcable';
const DEFAULT_IMAGE = 'https://filmcable.net/favicon.png';
const DEFAULT_DESCRIPTION = 'Filmcable is a film media publisher delivering authoritative coverage of cinema, film festivals, and the global film industry.';

export default function SEO({ 
  title, 
  description, 
  image, 
  url, 
  type = 'website',
  article = null 
}) {
  const seoTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Film Media Publisher`;
  const seoDescription = description || DEFAULT_DESCRIPTION;
  const seoImage = image || DEFAULT_IMAGE;
  const seoUrl = url || SITE_URL;

  return (
    <Helmet prioritizeSeoTags>
      {/* Basic Meta Tags */}
      <title>{seoTitle}</title>
      <meta name="title" content={seoTitle} />
      <meta name="description" content={seoDescription} />
      <link rel="canonical" href={seoUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type === 'article' ? 'article' : 'website'} />
      <meta property="og:url" content={seoUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:image" content={seoImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={seoUrl} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />

      {/* Article Specific Tags */}
      {article && (
        <>
          <meta property="article:published_time" content={article.created_at} />
          <meta property="article:modified_time" content={article.updated_at} />
          <meta property="article:author" content={article.author} />
          <meta property="article:section" content={article.category} />
        </>
      )}

      {/* Structured Data for Articles */}
      {article && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            "headline": title,
            "description": seoDescription,
            "image": [seoImage],
            "author": {
              "@type": "Person",
              "name": article.author
            },
            "publisher": {
              "@type": "Organization",
              "name": "Filmcable",
              "logo": {
                "@type": "ImageObject",
                "url": DEFAULT_IMAGE
              }
            },
            "datePublished": article.created_at,
            "dateModified": article.updated_at,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": seoUrl
            }
          })}
        </script>
      )}

      {/* Organization Structured Data for Homepage */}
      {type === 'website' && !article && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Filmcable",
            "url": SITE_URL,
            "logo": DEFAULT_IMAGE,
            "description": seoDescription,
            "sameAs": [
              "https://instagram.com/filmcable",
              "https://tiktok.com/@filmcable_"
            ]
          })}
        </script>
      )}
    </Helmet>
  );
}
